import { Link, useParams } from 'react-router-dom';
import PageHero from 'components/shared/PageHero';
import { useResource } from 'hooks/useResource';
import { getAdmissionResources } from 'services/content';
import { institutions } from './admissions/admissionRouting';
import PaymentWalkthrough from 'components/payment/PaymentWalkthrough';

/**
 * Admissions page — the fee payment guideline, and nothing else.
 *
 * The school admits in person, so the round dates, the open-or-closed status,
 * the class and fee list and every route into the online portal are gone. What
 * a family needs from this page is how to pay, so that is all it carries.
 *
 * The guide is chosen by institution tag and edited in Admin > Payment & Forms,
 * which still holds the downloadable forms as well — nothing renders those at
 * the moment.
 */
export default function AdmissionsPage() {
  const { inst } = useParams();
  const { data: resources, loading, error } = useResource(getAdmissionResources);

  // The usual tags convention: no tags means it shows on every institution.
  const guide = (resources?.paymentGuides || []).find(
    (g) => !g?.tags?.length || g.tags.includes(inst)
  );

  if (!institutions[inst])
    return (
      <div className="mx-auto max-w-4xl p-8">
        <h1>Institution not found</h1>
        <Link to="/">Back to the homepage</Link>
      </div>
    );

  return (
    <>
      <PageHero
        title="ভর্তি তথ্য / Admission information"
        sub={institutions[inst]}
        page="Admissions"
      />
      <main className="mx-auto max-w-4xl space-y-7 px-5 py-10">
        <section className="rounded-2xl border border-line bg-white p-6 sm:p-8 space-y-5">
          <h2 className="text-2xl font-bold text-green-dark">{institutions[inst]}</h2>

          {loading ? (
            <p>Loading…</p>
          ) : error ? (
            <p role="alert">
              The payment guideline could not be loaded. Please try again later, or contact the
              school office.
            </p>
          ) : guide ? (
            <PaymentWalkthrough guide={guide} />
          ) : (
            // Better than an empty card: tell them where to ask.
            <p className="text-muted">
              The payment guideline has not been published yet. Please contact the school office.
            </p>
          )}
        </section>
      </main>
    </>
  );
}
